import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Shake / Rotation Counter — two counters in parallel:
//   - Shake count: discrete acceleration peaks above a threshold,
//     debounced with a refractory period so one shake doesn't count
//     twice.
//   - Rotation count: integrates gyroscope rotation rate over time;
//     every full 360° of integrated rotation increments the counter.
//
// Both counters are reset by user gesture.

const SHAKE_THRESHOLD_MS2 = 14; // m/s² above which we count a shake
const SHAKE_REFRACTORY_MS = 250; // minimum gap between consecutive shakes

export default function ShakeRotationCounter() {
  const [state, setState] = useState("idle");
  const [shakes, setShakes] = useState(0);
  const [rotations, setRotations] = useState(0);
  const [rate, setRate] = useState(0);
  const [activeAxis, setActiveAxis] = useState("z"); // x | y | z
  const motionHandler = useRef(null);
  const lastShakeRef = useRef(0);
  const integratedAngleRef = useRef(0); // degrees
  const lastTimeRef = useRef(0);

  const enable = async () => {
    if (typeof DeviceMotionEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceMotionEvent.requestPermission === "function") {
        const r = await DeviceMotionEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      motionHandler.current = (e) => {
        const now = performance.now();
        // Shake detection
        const a = e.acceleration || e.accelerationIncludingGravity;
        if (a) {
          const mag = Math.sqrt(
            Math.pow(a.x || 0, 2) +
            Math.pow(a.y || 0, 2) +
            Math.pow(a.z || 0, 2)
          );
          // For accelerationIncludingGravity, subtract gravity
          const linMag = e.acceleration ? mag : Math.abs(mag - 9.81);
          if (linMag > SHAKE_THRESHOLD_MS2 && now - lastShakeRef.current > SHAKE_REFRACTORY_MS) {
            lastShakeRef.current = now;
            setShakes((s) => s + 1);
          }
        }
        // Rotation integration: rotationRate is deg/s
        const r = e.rotationRate;
        if (r && lastTimeRef.current) {
          const dt = (now - lastTimeRef.current) / 1000;
          const axisRate = activeAxisRef.current === "x" ? (r.beta || 0)
                         : activeAxisRef.current === "y" ? (r.gamma || 0)
                         :                                 (r.alpha || 0);
          setRate(axisRate);
          integratedAngleRef.current += Math.abs(axisRate) * dt;
          if (integratedAngleRef.current >= 360) {
            const fullTurns = Math.floor(integratedAngleRef.current / 360);
            integratedAngleRef.current -= fullTurns * 360;
            setRotations((rot) => rot + fullTurns);
          }
        }
        lastTimeRef.current = now;
      };
      window.addEventListener("devicemotion", motionHandler.current);
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  // Keep active axis in a ref so the handler sees fresh value
  const activeAxisRef = useRef(activeAxis);
  activeAxisRef.current = activeAxis;

  useEffect(() => () => {
    if (motionHandler.current) window.removeEventListener("devicemotion", motionHandler.current);
  }, []);

  const resetShakes = () => { setShakes(0); lastShakeRef.current = 0; };
  const resetRotations = () => { setRotations(0); integratedAngleRef.current = 0; };

  return (
    <ToolsShell title="Shake / Rotation">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Counts sharp shakes and full rotations of your phone. Useful for paint cans, exercise reps, prop counting.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Motion sensor" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Card label="Shakes" value={shakes} onReset={resetShakes} />
            <Card label="Rotations" value={rotations} onReset={resetRotations} sub={`${Math.round(integratedAngleRef.current)}° / 360°`} />
          </div>

          <div style={{ marginTop: 18, padding: "14px 16px", background: "#13141F", border: "1px solid #262839", borderRadius: 10 }}>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 8 }}>Rotation axis to count</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ id: "x", label: "X (pitch)" }, { id: "y", label: "Y (roll)" }, { id: "z", label: "Z (yaw)" }].map((ax) => (
                <button
                  key={ax.id}
                  onClick={() => setActiveAxis(ax.id)}
                  style={{ background: activeAxis === ax.id ? TOOLS_ACCENT : "#1C1E2E", color: activeAxis === ax.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1 }}
                >
                  {ax.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#7C8099", marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              Live rate: {rate.toFixed(0)}°/s
            </div>
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Shake threshold ~14 m/s² (sharper than walking, gentler than a hard rattle). Rotation counted by integrating gyroscope rate over time; gyros drift, so very slow rotations may register low.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function Card({ label, value, sub, onReset }) {
  return (
    <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "16px 18px", textAlign: "center" }}>
      <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#5B5E7A", marginTop: 4 }}>{sub}</div>}
      <button onClick={onReset} style={{ marginTop: 10, background: "transparent", color: "#7C8099", border: "1px solid #262839", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
        Reset
      </button>
    </div>
  );
}
